import { useMemo } from 'react'

import {
  ExternalSearchResult,
  Member,
  Post,
  SearchEntityType,
  Space,
} from '@tribeplatform/gql-client/types'
import { useSearch } from '@tribeplatform/react-sdk/hooks'

import { LEGACY_SEARCH_ENTITY_TYPE_MAP } from '../../SmartSearch.constants.js'
import {
  LegacySearchQueryResult,
  LegacySearchResultsType,
  SmartSearchEntityType,
} from '../../SmartSearch.types.js'

import {
  mapHitToSmartSearchExternal,
  mapHitToSmartSearchMember,
  mapHitToSmartSearchPost,
  mapHitToSmartSearchSpace,
} from './legacy-search-utils.js'

interface Props {
  query: string
  type: SmartSearchEntityType
  spaceIds: string[]
}

interface UseQueryLegacySearchResult {
  posts: LegacySearchQueryResult<Post>
  spaces: LegacySearchQueryResult<Space>
  members: LegacySearchQueryResult<Member>
  externals: LegacySearchQueryResult<ExternalSearchResult>
}

/**
 * This hook is a mapper for the legacy search.
 * Calls the useSearch hook and maps the results to the new smart search results.
 */
export const useQueryLegacySearch = ({
  query,
  type,
  spaceIds,
}: Props): UseQueryLegacySearchResult => {
  const legacyType = Object.entries(LEGACY_SEARCH_ENTITY_TYPE_MAP).find(
    ([_, legacyType]) => legacyType === type,
  )?.[0]

  const queryWithFilters = useMemo(() => {
    let newQuery = query
    if (type) {
      newQuery += ` +for:${legacyType}`
    }

    // single space: +in:spaceId
    // multiple spaces: +in~spaceId1,spaceId2
    if (
      spaceIds.length > 0 &&
      (!type || type === SmartSearchEntityType.POSTS)
    ) {
      newQuery += ` +in${spaceIds.length === 1 ? ':' : '~'}${spaceIds.join(
        ',',
      )}`
    }

    return newQuery
  }, [query, type, spaceIds, legacyType])

  const { data, isLoading, isError } = useSearch({
    variables: {
      input: {
        includeExternal: true,
        query: queryWithFilters,
      },
    },
    fields: {
      entity: 'all',
      by: 'all',
      media: 'all',
      in: 'all',
    },
  })

  const { posts, spaces, members, externals }: LegacySearchResultsType =
    useMemo(() => {
      if (!data?.hits) {
        return { posts: [], spaces: [], members: [], externals: [] }
      }

      return data.hits.reduce(
        (acc, hit) => {
          switch (hit.entityType) {
            case SearchEntityType.post:
              acc.posts.push(...hit.hits.map(mapHitToSmartSearchPost))
              break
            case SearchEntityType.space:
              acc.spaces.push(...hit.hits.map(mapHitToSmartSearchSpace))
              break
            case SearchEntityType.member:
              acc.members.push(...hit.hits.map(mapHitToSmartSearchMember))
              break
            case SearchEntityType.External:
              acc.externals.push(...hit.hits.map(mapHitToSmartSearchExternal))
              break
            default:
              break
          }
          return acc
        },
        { posts: [], spaces: [], members: [], externals: [] },
      )
    }, [data?.hits])

  return {
    posts: {
      data: posts,
      isLoading,
      isError,
      isFetching: false,
    },
    spaces: {
      data: spaces,
      isLoading,
      isError,
      isFetching: false,
    },
    members: {
      data: members,
      isLoading,
      isError,
      isFetching: false,
    },
    externals: {
      data: externals,
      isLoading,
      isError,
      isFetching: false,
    },
  }
}
